import React, { useRef } from 'react';
import { CustomFlowbiteTheme, Datepicker } from 'flowbite-react';
import { getDatePickerTheme } from '../../styles/themes/datePicker';
import { DatepickerRef } from 'flowbite-react/lib/esm/components/Datepicker/Datepicker';

/* 0 for Sunday, 6 for Saturday */
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type DateInputTextSize = 'xs' | 'sm';

const now = new Date();

export type DatePickerProps = {
    className?: string;
    id?: string;
    testId?: string;
    ariaLabel?: string;
    title?: string;
    autoHide?: boolean;
    inline?: boolean;
    weekStart?: WeekStart;
    minDate?: Date;
    maxDate?: Date;
    defaultDate?: Date; // pass it to enable 'clear' functionality (reset)
    selectedDate?: Date;
    handleDateChange: (date: Date) => void;
    showTodayButton?: boolean;
    showClearButton?: boolean;
    labelTodayButton?: string;
    labelClearButton?: string;
    customDateFormatter?: (date: Date) => string;
    hideCalendarIcon?: boolean;
    inputTextSize?: DateInputTextSize;
};

/* see this page for ref on some props: https://www.npmjs.com/package/tailwind-datepicker-react */
export const DatePicker: React.FC<DatePickerProps> = (props) => {
    const {
        className,
        testId,
        ariaLabel,
        title = '',
        autoHide = false,
        inline = false,
        weekStart = 0, // Sunday
        minDate,
        maxDate,
        defaultDate,
        selectedDate,
        handleDateChange,
        showTodayButton = true,
        showClearButton = true,
        labelTodayButton,
        labelClearButton,
        customDateFormatter,
        hideCalendarIcon = true,
        inputTextSize = 'sm',
    } = props;
    const datepickerRef = useRef<DatepickerRef | null>(null);

    /* optional customDateFormatter, or fallback to .toLocaleDateString() */
    const formattedDate = (d: Date): string => {
        const output = customDateFormatter ? customDateFormatter(d) : d.toLocaleDateString();
        return output;
    };

    /* passing props to modify theme dynamically */
    const dynamicTheme: CustomFlowbiteTheme['datepicker'] = getDatePickerTheme(hideCalendarIcon, inputTextSize);

    return (
        <Datepicker
            ref={datepickerRef}
            theme={dynamicTheme}
            className={className}
            data-testid={testId}
            aria-label={ariaLabel}
            title={title}
            autoHide={autoHide}
            inline={inline}
            weekStart={weekStart}
            minDate={minDate}
            maxDate={maxDate}
            showTodayButton={showTodayButton}
            showClearButton={showClearButton}
            labelTodayButton={labelTodayButton}
            labelClearButton={labelClearButton}
            value={formattedDate(selectedDate ?? now)}
            defaultDate={defaultDate ?? selectedDate ?? now} // set on first render
            onSelectedDateChanged={(newDate) => handleDateChange(newDate)}
        />
    );
};

/* (Date Range) Dual Picker initial */
export type DualDatePickerProps = { start: DatePickerProps; end: DatePickerProps };

export const DualDatePicker: React.FC<DualDatePickerProps> = (props) => {
    const { start, end } = props;

    return (
        <div className={`flex flex-row w-full`}>
            <DatePicker
                className={start.className}
                data-testid={start.testId}
                title={start.title ?? 'Start'}
                autoHide={start.autoHide}
                inline={start.inline}
                weekStart={start.weekStart}
                minDate={start.minDate}
                maxDate={start.maxDate}
                selectedDate={start.selectedDate}
                defaultDate={start.defaultDate}
                handleDateChange={start.handleDateChange}
                showTodayButton={true}
                showClearButton={false}
            />
            <DatePicker
                className={end.className}
                data-testid={end.testId}
                title={end.title ?? 'End'}
                autoHide={end.autoHide}
                inline={end.inline}
                weekStart={end.weekStart}
                minDate={end.minDate}
                maxDate={end.maxDate}
                selectedDate={end.selectedDate}
                defaultDate={end.defaultDate}
                handleDateChange={end.handleDateChange}
                showTodayButton={false}
                showClearButton={true}
            />
        </div>
    );
};
